import { showEmeraldDialog } from 'src/common/components/interactive-dialogue/components/interactive-dialog-content/interactive-dialog-content';
import { popEmeraldModal } from 'src/common/emerald-modal-manager/emerald-modal-manager';
import { ActivationStatus } from 'src/common/enumerations/activation-status';
import { AuthorizationStatus } from 'src/common/enumerations/authorization-status';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import { EntryMethod } from 'src/common/enumerations/entry-method';
import { getEJItemBySequenceNumber, getItemByLineId } from 'src/common/helpers/item-list-helper';
import { getValueAsNumber } from 'src/common/helpers/number-helper';
import { createSpan, endSpan } from 'src/common/helpers/tracing-helper';
import { IMessageModal, MessageModalSizeEnum } from 'src/common/interfaces/dtos/IMessageModal';
import { IElectronicJournalRecord } from 'src/common/interfaces/states/IElectronicJournalRecord';
import { PaymentMessage, ReceiptSection, Status } from 'src/devices/payment/dtos/eps-data';
import {
  activateOnlineItem,
  authorizeOnlineItemDto,
  beginPaymentSession,
  cancelPinpadOperation,
  endPaymentSession,
  inPaymentSession,
  reloadOnlineItem,
  voidPaymentforOnlineItem,
} from 'src/devices/payment/pinpad-operations';
import { CartItemActivation, OnlineItemServiceType } from 'src/online-items/dto/CartItemActivation';

const modalId = 'OnlineItemAuthorizationModal';
export const showAuthorizingModal = (): void => {
  const message: IMessageModal = {
    title: 'OnlineItem',
    message: 'OnlineItemAuthorization',
    subMessage: 'OnlineItemWait',
    type: EmeraldDialogTypes.Info,
    size: MessageModalSizeEnum.Medium,
    hideButtons: true,
    disableBackdropClick: true,
    dialogHeight: 47.8,
    modalId,
  };
  showEmeraldDialog(message);
};

export const closeAuthorizingModal = () => {
  popEmeraldModal(modalId);
};

const OfflineGiftCardActivationModal = 'OfflineGiftCardActivationModal';
export const showOfflineGiftCardActivationModal = (): void => {
  const message: IMessageModal = {
    message: 'OfflineGiftCardActivation',
    subMessage: 'OfflineGiftCardActivationMessage',
    type: EmeraldDialogTypes.Info,
    size: MessageModalSizeEnum.Medium,
    modalId: OfflineGiftCardActivationModal,
    closeButtonLabel: 'Ok',
    hideAcceptButton: true,
    onCancel: () => {
      popEmeraldModal(OfflineGiftCardActivationModal);
    },
  };
  showEmeraldDialog(message);
};

export const performOnlineItemAuthorization = async (
  restrictedLineId: string,
  onActivateApproval: (restrictedLine: string, activationData: CartItemActivation) => void,
): Promise<void> => {
  const span = createSpan('perform-online-item-authorization');
  await cancelPinpadOperation();
  await endPaymentSession();
  await beginPaymentSession();
  const item = getEJItemBySequenceNumber(getValueAsNumber(restrictedLineId));
  const itemByLineId = getItemByLineId(getValueAsNumber(restrictedLineId));
  const price = item?.Price ?? '';
  const externalId = item?.OnlineItem?.authorizationExternalId ?? '';
  const upc = item?.ItemId ?? '';
  const entryMethod = externalId && upc ? EntryMethod.Barcode : itemByLineId?.entryMethod;

  let authorizationStatus = AuthorizationStatus.Decline;
  let activationData: PaymentMessage;
  let activationAuthCode: string | undefined = '';
  let activationPaymentTranID: string | undefined = '';
  let receiptBodyLine: string[] = [];
  let authorizationDateTime: string | undefined = '';
  const authorizeOnlineItemData: authorizeOnlineItemDto = {
    externalId,
    price,
    upc,
    entryMethod,
  };

  try {
    const authorizeOnlineItem =
      item?.OnlineItem?.serviceType === OnlineItemServiceType.Reload
        ? reloadOnlineItem
        : activateOnlineItem;

    activationData = await authorizeOnlineItem(
      authorizeOnlineItemData,
      span.spanContext(),
    ).finally(() => endPaymentSession());

    activationData.receipts = activationData.receipts || [];
    if (activationData.receipts) {
      receiptBodyLine = activationData.receipts.filter(
        ReceiptData => ReceiptData.receiptSection === ReceiptSection.Body,
      )[0].receiptLines;
    }
    if (activationData.status === Status.Success) {
      authorizationDateTime = new Date().toISOString();
      authorizationStatus = AuthorizationStatus.Success;
      if (activationData?.paymentDetails?.offline) {
        authorizationDateTime = undefined;
        authorizationStatus = AuthorizationStatus.Offline;
      }
    }
    activationAuthCode = activationData.paymentDetails?.authCode;
    activationPaymentTranID = activationData.paymentDetails?.paymentTranID;
  } catch (error) {
    authorizationStatus = AuthorizationStatus.Decline;
  }

  const activationDataDTO: CartItemActivation = {
    additionalData: {
      onlineItem: {
        receiptText: receiptBodyLine,
      },
      activation: {
        status: ActivationStatus.Activated,
        activationData: {
          authorizationStatus: authorizationStatus,
          authorizationCode: activationAuthCode,
          referenceNumber: activationPaymentTranID,
          authorizationDateTime: authorizationDateTime,
        },
      },
    },
  };
  onActivateApproval(restrictedLineId, activationDataDTO);
  endSpan(span);
};

export const performOnlineItemDeactivation = async (
  ejItem: IElectronicJournalRecord,
): Promise<CartItemActivation> => {
  const span = createSpan('perform-online-item-authorization');
  showAuthorizingModal();
  const price = ejItem?.Price ?? '';
  const externalId = ejItem?.OnlineItem?.authorizationExternalId ?? '';

  let authorizationStatus = AuthorizationStatus.Decline;
  let activationData: PaymentMessage;
  let activationAuthCode: string | undefined = '';
  let activationPaymentTranID: string | undefined = '';

  try {
    if (!inPaymentSession()) {
      await beginPaymentSession();
    } else {
      await cancelPinpadOperation();
    }

    const item = getItemByLineId(ejItem.SequenceNumber);
    activationData = await voidPaymentforOnlineItem(
      externalId,
      item?.additionalData?.activation?.activationData?.referenceNumber || '',
      price,
    );

    if (activationData.status === Status.Success) {
      authorizationStatus = AuthorizationStatus.Success;
    }

    activationAuthCode = activationData.paymentDetails?.authCode;
    activationPaymentTranID = activationData.paymentDetails?.paymentTranID;
  } catch (error) {
    authorizationStatus = AuthorizationStatus.Decline;
  }

  const deactivationDataDTO: CartItemActivation = {
    additionalData: {
      activation: {
        status: ActivationStatus.Deactivated,
        deactivationData: {
          authorizationStatus: authorizationStatus,
          authorizationCode: activationAuthCode || '123',
          referenceNumber: activationPaymentTranID,
          authorizationDateTime: new Date().toISOString(),
        },
      },
    },
  };
  closeAuthorizingModal();
  endSpan(span);
  return deactivationDataDTO;
};
